import React, { FC, ReactElement, useContext } from 'react'
import { useFormContext, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { NotificationsLanguage, NotificationsLanguageModal, UserNotificationTypesHelp } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { ALL_VALUE } from '../../../Common/API/ItemsGetters'
import Checkbox from '../../../Common/Components/Checkbox'
import FormRow from '../../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../../Common/Components/Heading'
import Label from '../../../Common/Components/Label'
import TextInput from '../../../Common/Components/TextInput'
import { ValidationErrors } from '../../../Common/Components/ValidationErrors/ValidationErrors'
import { LanguageContext } from '../../../Common/Contexts'
import {
    NOTIFICATION_NAME,
    notificationChannelsMapToString,
    notificationOrder,
    USER_NOTIFICATION,
    UserNotificationTypes
} from '../Constants'
import { DO_NOT_DISTURB_NAME, NOTIFICATION_CHANNEL_NAME, NotificationUserFieldNameSpacing } from '../FieldNames'
import { NotificationOrder } from '../Types'
import { UserNotificationsType } from '../../../../NonReact/common/Types/Types'

type Props = {
    UserNotifications: UserNotificationsType[]
}

type UserNotificationHelperProps = {
    children: ReactElement
    helpText: string
    showHelp: boolean
    index: number
}

const UserNotificationHelper: FC<UserNotificationHelperProps> = ({
    children,
    helpText,
    showHelp,
    index
}: UserNotificationHelperProps) => (
    <div className='usernotification-helper'>
        {children}
        {showHelp ? (
            <ValidationErrors name={NotificationUserFieldNameSpacing(`${UserNotificationTypes.THRESHOLD}${index}`)}>
                <Label customClass='help-label'>{helpText}</Label>
            </ValidationErrors>
        ) : (
            <></>
        )}
    </div>
)

export const UserNotificationsComponent: FC<Props> = ({ UserNotifications }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(UserNotificationTypesHelp.NAME)
    language.loadBundle(NotificationsLanguage.NAME)
    language.loadBundle(NotificationsLanguageModal.NAME)

    // Contexts
    const { watch }: UseFormMethods = useFormContext()

    const doNotDisturb: boolean = watch<string, boolean>(DO_NOT_DISTURB_NAME)
    const channels: string[] = watch<string, string[]>(NOTIFICATION_CHANNEL_NAME)

    const isNotificationGrayedOut = (name: string): boolean => {
        const notification: Definable<NotificationOrder> = notificationOrder.find(
            (notificationItem: NotificationOrder): boolean => notificationItem.name === name
        )

        return notification
            ? notification.enabledChannels.some((channel: string): boolean =>
                  channels
                      .map((channel2: string): string => notificationChannelsMapToString[Number(channel2)])
                      .includes(channel)
              )
            : true
    }

    const showHelpText = (name: string): boolean => {
        const notification: Definable<NotificationOrder> = notificationOrder.find(
            (notificationItem: NotificationOrder): boolean => notificationItem.name === name
        )

        return notification ? notification.showHelpText : true
    }

    return (
        <div className='notifications-tab tab-area'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(NotificationsLanguageModal.TabPreferencesHeadingUserNotifications)}
            </Heading>
            {UserNotifications.map(
                (userNotification: UserNotificationsType, index: number): ReactElement => (
                    <UserNotificationHelper
                        key={`${userNotification.Name}-${index}`}
                        helpText={language.get(`${USER_NOTIFICATION}${userNotification.Name}`)}
                        showHelp={showHelpText(userNotification.Name)}
                        index={index}
                    >
                        <FormRow isInline>
                            <Label customClass='label-left'>
                                {language.get(`${NOTIFICATION_NAME}${userNotification.Name}`)}
                            </Label>
                            <Checkbox
                                name={NotificationUserFieldNameSpacing(`${UserNotificationTypes.STATUS}${index}`)}
                                defaultChecked={false}
                                disabled={!doNotDisturb || !isNotificationGrayedOut(userNotification.Name)}
                            />
                            {userNotification.Threshold !== ALL_VALUE ? (
                                <TextInput
                                    placeholder={language.get(
                                        NotificationsLanguageModal.NotificationPrefereencesThreshholdPlaceholder
                                    )}
                                    inputName={NotificationUserFieldNameSpacing(
                                        `${UserNotificationTypes.THRESHOLD}${index}`
                                    )}
                                    rules={{
                                        required: language.get(
                                            NotificationsLanguageModal.NotificationPrefereencesThreshholdRequired
                                        ),
                                        pattern: {
                                            value: /^[0-9]*$/,
                                            message: language.get(
                                                NotificationsLanguageModal.NotificationPrefereencesThreshholdInvalid
                                            )
                                        }
                                    }}
                                    disabled={
                                        !doNotDisturb ||
                                        !isNotificationGrayedOut(userNotification.Name) ||
                                        userNotification.Threshold === ALL_VALUE ||
                                        !watch(
                                            NotificationUserFieldNameSpacing(`${UserNotificationTypes.STATUS}${index}`)
                                        )
                                    }
                                />
                            ) : (
                                <></>
                            )}
                        </FormRow>
                    </UserNotificationHelper>
                )
            )}
        </div>
    )
}
